// src/components/SuccessDialog.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { keyframes } from "@emotion/react";
import party from "party-js";
import IconTimeCapsuleClosedMarker from "../assets/icons/v2/time-capsule-closed.webp";
import IconTimeCapsuleLockedMarker from "../assets/icons/v2/time-capsule-locked.webp";

// Firebase
import { getAuth } from "firebase/auth";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";

const capsuleFloat = keyframes`
  0% { transform: translateY(4px) scale(0.92); opacity: 0; }
  45% { transform: translateY(-5px) scale(1.06); opacity: 1; }
  100% { transform: translateY(0) scale(1); opacity: 1; }
`;

const glowPulse = keyframes`
  0% { transform: scale(0.72); opacity: 0.72; }
  70% { transform: scale(1.45); opacity: 0; }
  100% { transform: scale(1.45); opacity: 0; }
`;

const sparklePop = keyframes`
  0% { transform: translate(-50%, -50%) rotate(var(--sparkle-rotation)) translateY(0) scale(0); opacity: 0; }
  18% { opacity: 1; }
  100% { transform: translate(-50%, -50%) rotate(var(--sparkle-rotation)) translateY(-112px) scale(1); opacity: 0; }
`;

const rewardRise = keyframes`
  0% { transform: translateY(8px) scale(0.96); opacity: 0; }
  100% { transform: translateY(0) scale(1); opacity: 1; }
`;

const buttonReveal = keyframes`
  0% { transform: translateY(8px) scale(0.96); opacity: 0; }
  100% { transform: translateY(0) scale(1); opacity: 1; }
`;

const SPARKLES = [
  { rotation: -72, delay: 0 },
  { rotation: -38, delay: 70 },
  { rotation: -12, delay: 25 },
  { rotation: 20, delay: 95 },
  { rotation: 48, delay: 45 },
  { rotation: 76, delay: 120 },
];

function asInt(value, fallback = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.trunc(n);
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return Number(value.toMillis() || 0);
  if (typeof value?.seconds === "number") {
    return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1000000);
  }
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

export default function SuccessDialog({
  sentCapsuleId,
  trigger,
  onClose,
  onPrimaryAction,
  payload,
  storageKey,
  autoCelebrateReward = true,
  celebrationCloseDelayMs = 2800,
  capsuleBuriedSuccessMinDurationMs = 1200,
}) {
  const resolvedKey =
    storageKey ||
    (sentCapsuleId ? `hasShownSuccess-${sentCapsuleId}` : "hasShownSuccess-global");

  const auth = getAuth();
  const userId = auth.currentUser?.uid || null;

  const type = payload?.type || "";
  const isCapsuleBuried = type === "capsule_buried";
  const isInsider = type === "insider_joined";

  const titleText = payload?.title || (isCapsuleBuried ? "Successfully buried!" : "Success!");
  const subtitleText = payload?.subtitle || "";
  const warningText = payload?.warning || null;

  const revealMs = useMemo(
    () => toMillis(payload?.revealDate || payload?.capsulePreview?.revealDate),
    [payload?.capsulePreview?.revealDate, payload?.revealDate]
  );
  const hasFutureRevealDate = revealMs > Date.now();

  const receiptId = useMemo(() => {
    if (payload?.receiptId) return String(payload.receiptId);
    if (isInsider) return "insider_free_signup";
    if (sentCapsuleId) return `tc_drop_${sentCapsuleId}`;
    return null;
  }, [payload?.receiptId, isInsider, sentCapsuleId]);

  const payloadPoints = useMemo(
    () => Math.max(0, asInt(payload?.rewardPoints ?? 0, 0)),
    [payload?.rewardPoints]
  );

  const [open, setOpen] = useState(false);
  const [resolvedEarnedPoints, setResolvedEarnedPoints] = useState(null);
  const [displayPoints, setDisplayPoints] = useState(0);
  const [showPrimary, setShowPrimary] = useState(false);
  const [celebrationActive, setCelebrationActive] = useState(false);

  const cardRef = useRef(null);
  const capsuleRef = useRef(null);
  const pointsRef = useRef(null);
  const timersRef = useRef([]);
  const frameRef = useRef(0);
  const hasOpenedCelebrationRef = useRef(false);

  const pointsEarned = useMemo(() => {
    const n = resolvedEarnedPoints == null ? payloadPoints : asInt(resolvedEarnedPoints, 0);
    return Math.max(0, n);
  }, [payloadPoints, resolvedEarnedPoints]);

  const hasReward = pointsEarned > 0;

  const clearScheduledWork = useCallback(() => {
    timersRef.current.forEach((timer) => window.clearTimeout(timer));
    timersRef.current = [];
    cancelAnimationFrame(frameRef.current);
  }, []);

  const schedule = useCallback((callback, delay) => {
    const timer = window.setTimeout(callback, delay);
    timersRef.current.push(timer);
    return timer;
  }, []);

  const prefersReducedMotion = useCallback(() => {
    if (typeof window === "undefined") return false;
    return Boolean(
      window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }, []);

  const fireParty = useCallback(() => {
    if (prefersReducedMotion()) return;
    const target = capsuleRef.current || cardRef.current || document.body;
    if (!target) return;

    party.confetti(target, {
      count: party.variation.range(76, 104),
      spread: 82,
      speed: party.variation.range(520, 860),
      size: party.variation.range(1, 1.55),
      shapes: ["star", "circle", "roundedSquare"],
    });
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (!trigger) return;

    const already = Boolean(sessionStorage.getItem(resolvedKey));
    if (already) return;

    sessionStorage.setItem(resolvedKey, "true");
    clearScheduledWork();
    hasOpenedCelebrationRef.current = false;
    setResolvedEarnedPoints(null);
    setDisplayPoints(0);
    setShowPrimary(!isCapsuleBuried);
    setCelebrationActive(false);
    setOpen(true);
  }, [clearScheduledWork, isCapsuleBuried, resolvedKey, trigger]);

  useEffect(() => {
    return clearScheduledWork;
  }, [clearScheduledWork]);

  useEffect(() => {
    if (!open) return undefined;
    if (!isCapsuleBuried) return undefined;

    const delayMs = Math.max(0, Number(capsuleBuriedSuccessMinDurationMs) || 0);
    const timer = schedule(() => setShowPrimary(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [capsuleBuriedSuccessMinDurationMs, isCapsuleBuried, open, schedule]);

  useEffect(() => {
    if (!open) return undefined;
    if (!userId) return undefined;

    // For a buried capsule, the total earned is the drop reward PLUS the
    // up-front per-recipient rewards, which land in separate receipts that all
    // share this capsuleId (tc_drop_* and tc_pickup_*). Sum them so the dialog
    // reflects the same combined total the ledger shows (e.g. +6 for a bury to
    // one contact) rather than just the drop receipt.
    if (isCapsuleBuried && sentCapsuleId) {
      const qRef = query(
        collection(db, "users", userId, "award_receipts"),
        where("meta.capsuleId", "==", sentCapsuleId)
      );
      const unsub = onSnapshot(
        qRef,
        (snap) => {
          if (snap.empty) return; // keep the optimistic preview until receipts land
          let sum = 0;
          snap.forEach((d) => {
            sum += asInt(d.data()?.deltaPoints, 0);
          });
          setResolvedEarnedPoints(Math.max(0, sum));
        },
        () => {}
      );
      return () => {
        if (typeof unsub === "function") unsub();
      };
    }

    // Non-capsule rewards (insider activation, or an explicit single receipt)
    // resolve from the one receipt.
    if (!receiptId) return undefined;

    const ref = doc(db, "users", userId, "award_receipts", receiptId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const pts = asInt(data?.deltaPoints, null);
        if (pts === null) return;
        setResolvedEarnedPoints(Math.max(0, pts));
      },
      () => {}
    );

    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [open, receiptId, userId, isCapsuleBuried, sentCapsuleId]);

  useEffect(() => {
    if (!open) return;
    if (!isCapsuleBuried) return;
    if (hasOpenedCelebrationRef.current) return;

    hasOpenedCelebrationRef.current = true;
    setCelebrationActive(true);
    frameRef.current = requestAnimationFrame(() => {
      schedule(fireParty, 170);
    });
    schedule(() => setCelebrationActive(false), 1800);
  }, [fireParty, isCapsuleBuried, open, schedule]);

  useEffect(() => {
    if (!open) return undefined;
    if (!hasReward) {
      setDisplayPoints(0);
      return undefined;
    }

    if (prefersReducedMotion()) {
      setDisplayPoints(pointsEarned);
      return undefined;
    }

    let raf = 0;
    let start = 0;
    const durationMs = 950;

    const tick = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min(1, (timestamp - start) / durationMs);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayPoints(Math.max(1, Math.floor(pointsEarned * eased)));
      if (progress < 1) raf = requestAnimationFrame(tick);
      else setDisplayPoints(pointsEarned);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [hasReward, open, pointsEarned, prefersReducedMotion]);

  const close = () => {
    clearScheduledWork();
    setOpen(false);
    onClose?.();
  };

  const handlePrimaryClick = async () => {
    try {
      if (typeof onPrimaryAction === "function") {
        await onPrimaryAction();
      }
    } catch {
      // Keep the success dialog close path reliable even if a caller action fails.
    } finally {
      close();
    }
  };

  const renderCapsuleVisual = () => (
    <Box
      ref={capsuleRef}
      data-testid="capsule-buried-celebration-source"
      sx={{
        position: "relative",
        width: 88,
        height: 88,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
      }}
    >
      <Box
        sx={(theme) => ({
          position: "absolute",
          inset: 8,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(theme.colors.white, 0.22)} 0%, ${alpha(
            theme.colors.white,
            0
          )} 68%)`,
          animation: `${glowPulse} 1300ms ease-out 1`,
        })}
      />
      {celebrationActive
        ? SPARKLES.map((sparkle) => (
            <Box
              key={`${sparkle.rotation}-${sparkle.delay}`}
              sx={(theme) => ({
                "--sparkle-rotation": `${sparkle.rotation}deg`,
                position: "absolute",
                top: "50%",
                left: "50%",
                width: 9,
                height: 9,
                borderRadius: 1,
                backgroundColor: theme.colors.white,
                boxShadow: `0 0 14px ${alpha(theme.colors.white, 0.92)}`,
                animation: `${sparklePop} 920ms ease-out ${sparkle.delay}ms 1 both`,
              })}
            />
          ))
        : null}
      <Box
        component="img"
        src={hasFutureRevealDate ? IconTimeCapsuleLockedMarker : IconTimeCapsuleClosedMarker}
        alt="Success"
        sx={{
          width: 70,
          height: 70,
          objectFit: "contain",
          position: "relative",
          animation: `${capsuleFloat} 620ms cubic-bezier(0.2, 0.85, 0.28, 1.18) 1`,
        }}
      />
    </Box>
  );

  const renderReward = () => (
    <Box
      ref={pointsRef}
      data-testid="capsule-buried-reward-slot"
      sx={{
        width: "100%",
        height: 92,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {hasReward ? (
        <Box
          sx={(theme) => ({
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.25,
            width: "auto",
            maxWidth: "100%",
            minHeight: 76,
            px: 1.5,
            py: 1,
            borderRadius: 999,
            backgroundColor: alpha(theme.colors.white, 0.08),
            border: `1px solid ${alpha(theme.colors.white, 0.12)}`,
            boxShadow: `inset 0 0 0 1px ${alpha(theme.colors.white, 0.05)}, 0 12px 30px ${alpha(
              "#000",
              0.16
            )}`,
            animation: `${rewardRise} 420ms ease-out 1 both`,
          })}
        >
          <Box
            sx={(theme) => ({
              width: 58,
              height: 58,
              borderRadius: "50%",
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(145deg, ${alpha(theme.colors.white, 0.22)}, ${alpha(
                theme.colors.white,
                0.1
              )})`,
              boxShadow: `inset 0 0 0 1px ${alpha(theme.colors.white, 0.18)}, 0 10px 24px ${alpha(
                "#000",
                0.18
              )}`,
            })}
          >
            <Typography
              variant="h4"
              sx={(theme) => ({
                color: theme.colors.white,
                fontWeight: 900,
                lineHeight: 1,
              })}
            >
              {displayPoints}
            </Typography>
          </Box>

          <Typography
            variant="h6"
            sx={(theme) => ({
              color: theme.colors.white,
              fontWeight: 900,
              lineHeight: 1.1,
              whiteSpace: "normal",
              textAlign: "left",
            })}
          >
            {`+${pointsEarned} points earned`}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={(theme) => ({
            width: "100%",
            maxWidth: 304,
            minHeight: 82,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            border: `1px solid ${alpha(theme.colors.white, 0.1)}`,
            backgroundColor: alpha(theme.colors.white, 0.06),
          })}
        >
          <Typography
            variant="body2"
            sx={(theme) => ({
              color: alpha(theme.colors.white, 0.74),
              fontWeight: 700,
              textAlign: "center",
            })}
          >
            Sealing your reward...
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderCapsuleBuried = () => (
    <Box
      data-testid="capsule-buried-success-card"
      ref={cardRef}
      sx={{
        width: "100%",
        height: 278,
        display: "grid",
        gridTemplateRows: "84px 52px 92px",
        justifyItems: "center",
        alignItems: "center",
        alignContent: "center",
        rowGap: 0.75,
      }}
    >
      {renderCapsuleVisual()}

      <Box sx={{ textAlign: "center", alignSelf: "center", px: 1 }}>
        <Typography
          variant="h5"
          sx={(theme) => ({
            color: theme.colors.white,
            fontWeight: 900,
            lineHeight: 1.08,
          })}
        >
          {titleText}
        </Typography>
        <Typography
          variant="body2"
          sx={(theme) => ({
            mt: 0.5,
            color: alpha(theme.colors.white, 0.76),
            fontWeight: 700,
            lineHeight: 1.2,
          })}
        >
          {subtitleText || "Your capsule is sealed and on its way."}
        </Typography>
      </Box>

      {renderReward()}
    </Box>
  );

  const renderGenericIcon = () => {
    if (isInsider || hasReward) {
      return (
        <Box
          sx={(theme) => ({
            width: 82,
            height: 82,
            borderRadius: "50%",
            bgcolor: alpha(theme.colors.white, 0.16),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          })}
        >
          <WorkspacePremiumIcon sx={(theme) => ({ fontSize: 46, color: theme.colors.white })} />
        </Box>
      );
    }

    return (
      <Box
        component="img"
        src={hasFutureRevealDate ? IconTimeCapsuleLockedMarker : IconTimeCapsuleClosedMarker}
        alt="Success"
        sx={{ width: 80, height: 80 }}
      />
    );
  };

  const renderGeneric = () => (
    <Box
      sx={{
        width: "100%",
        minHeight: 260,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 2,
      }}
    >
      {renderGenericIcon()}
      <Typography variant="h5" sx={(theme) => ({ color: theme.colors.white, fontWeight: 800 })}>
        {isInsider && hasReward ? "You've earned" : titleText}
      </Typography>
      {hasReward ? (
        <>
          <Box
            sx={(theme) => ({
              width: 112,
              height: 112,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: alpha(theme.colors.white, 0.14),
              boxShadow: `inset 0 0 0 1px ${alpha(theme.colors.white, 0.12)}`,
            })}
          >
            <Typography
              variant="h2"
              sx={(theme) => ({ color: theme.colors.white, fontWeight: 900, lineHeight: 1 })}
            >
              {displayPoints}
            </Typography>
          </Box>
          <Typography variant="body1" sx={(theme) => ({ color: alpha(theme.colors.white, 0.9) })}>
            points earned
          </Typography>
        </>
      ) : null}
      {subtitleText ? (
        <Typography variant="body1" sx={(theme) => ({ color: alpha(theme.colors.white, 0.9) })}>
          {subtitleText}
        </Typography>
      ) : null}
      {warningText ? (
        <Alert
          severity="warning"
          sx={{
            mt: 1,
            width: "100%",
            maxWidth: 520,
            textAlign: "center",
            backgroundColor: (theme) => alpha(theme.colors.white, 0.9),
          }}
        >
          {warningText}
        </Alert>
      ) : null}
    </Box>
  );

  const canShowPrimary = !isCapsuleBuried || showPrimary;

  return (
    <Dialog
      open={open}
      onClose={close}
      disableScrollLock
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 40px)", sm: 374 },
          maxWidth: { xs: "calc(100% - 40px)", sm: 374 },
          minHeight: isCapsuleBuried ? 402 : 380,
          boxSizing: "border-box",
          m: { xs: 2.5, sm: 4 },
          p: isCapsuleBuried ? 2.25 : 3,
          borderRadius: 2,
          overflow: "hidden",
          textAlign: "center",
          backgroundColor: "transparent",
          backgroundImage: (theme) =>
            isCapsuleBuried
              ? `radial-gradient(circle at 50% 0%, ${alpha(theme.colors.white, 0.18)} 0%, ${alpha(
                  theme.colors.white,
                  0
                )} 34%), linear-gradient(180deg, ${alpha("#25224d", 0.98)} 0%, ${alpha(
                  "#151430",
                  0.98
                )} 100%)`
              : `linear-gradient(180deg, ${alpha(theme.colors.mediumBlue, 0.92)}, ${alpha(
                  theme.colors.mediumBlue,
                  0.88
                )})`,
          boxShadow: "0 24px 70px rgba(0, 0, 0, 0.34)",
          border: (theme) => `1px solid ${alpha(theme.colors.white, 0.12)}`,
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          width: "100%",
          minHeight: isCapsuleBuried ? 278 : 260,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isCapsuleBuried ? renderCapsuleBuried() : renderGeneric()}
      </DialogContent>

      <DialogActions
        data-testid="success-dialog-actions"
        sx={{
          height: 66,
          p: 0,
          pt: 0,
          pb: 1.5,
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        <Button
          variant="contained"
          onClick={handlePrimaryClick}
          aria-hidden={!canShowPrimary}
          tabIndex={canShowPrimary ? 0 : -1}
          style={{
            visibility: canShowPrimary ? "visible" : "hidden",
            pointerEvents: canShowPrimary ? "auto" : "none",
          }}
          sx={(theme) => ({
            minWidth: 90,
            borderRadius: 999,
            backgroundColor: theme.colors.white,
            color: theme.colors.blue,
            fontWeight: 900,
            fontSize: "1rem",
            px: 3,
            py: 1.2,
            boxShadow: canShowPrimary ? "0 10px 24px rgba(0, 0, 0, 0.2)" : "none",
            animation: canShowPrimary ? `${buttonReveal} 260ms ease-out 1 both` : "none",
          })}
        >
          OK!
        </Button>
      </DialogActions>
    </Dialog>
  );
}
